import { MechanicValidator } from './MechanicValidator.ts';
import { CardValidatorOrdered } from '@colonist/game-mechanic-validators';
import { PieceTypes } from '@colonist/game-base-types';
import { getCardsForPiece } from '@colonist/game-card-types';
import { PlayerColor, CardEnum, ActionState } from '@colonist/game-states';
import { ArrayUtils } from '@colonist/utils';
import { MechanicValidatorDevelopmentCardPlayer } from './MechanicValidatorDevelopmentCardPlayer.ts';
export class MechanicValidatorDevelopmentCard extends MechanicValidator {
    getDevelopmentCardsPlayerState(player) {
        const devCardsPlayerState = this.playerDevelopmentCardStates.get(player);
        if (devCardsPlayerState === undefined) throw new Error(`No MechanicDevelopmentCardsPlayerState for player ${player}`);
        return devCardsPlayerState;
    }
    hasResourcesForDevelopmentCard(player) {
        const playerController = this.playerValidators.get(player);
        if (playerController == undefined) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.noPlayer'
            }
        };
        const resourcesForDevelopmentCard = getCardsForPiece(PieceTypes.DevelopmentCard);
        const hasResourcesForDevelopmentCard = playerController.hasResourceCardsInHand(resourcesForDevelopmentCard);
        if (!hasResourcesForDevelopmentCard) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.notEnoughResources'
            }
        };
        return {
            canTakeAction: true
        };
    }
    isOpponentInBlockingState(card, player) {
        switch(card){
            case CardEnum.Knight:
                return this.currentStateValidator.isOpponentInKnightBlockingState(player);
            default:
                return false;
        }
    }
    canPlayerUseDevelopmentCard(player, card) {
        const isSomePlayerDiscarding = this.currentStateValidator.isSomePlayerInActionState(ActionState.SelectCardsToDiscard);
        if (isSomePlayerDiscarding) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.playersDiscarding'
            }
        };
        if (this.isOpponentInBlockingState(card, player)) return MechanicValidatorDevelopmentCard.getErrorMessageIfBlockedByOpponentActionState(card);
        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player);
        const developmentCards = new CardValidatorOrdered(devCardsPlayerState.developmentCards);
        const hasCard = developmentCards.cards.includes(card);
        if (hasCard === false) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.developmentCardNotInHand'
            }
        };
        const isVictoryPoint = card == CardEnum.VictoryPoint;
        if (isVictoryPoint) return {
            canTakeAction: true
        };
        if (devCardsPlayerState.hasUsedDevelopmentCardThisTurn) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.gameState.gameLogs.canOnlyPlayOneDevCardPerTurn'
            }
        };
        const currentCardCount = developmentCards.getCardCountForCard(card);
        var _devCardsPlayerState_developmentCardsBoughtThisTurn;
        const amountPurchasedThisTurn = ArrayUtils.countOccurenceOfValueInArray((_devCardsPlayerState_developmentCardsBoughtThisTurn = devCardsPlayerState.developmentCardsBoughtThisTurn) !== null && _devCardsPlayerState_developmentCardsBoughtThisTurn !== void 0 ? _devCardsPlayerState_developmentCardsBoughtThisTurn : [], card);
        if (currentCardCount <= amountPurchasedThisTurn) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.gameState.gameLogs.cannotPlayDevCardOnTurnYouBoughtIt'
            }
        };
        return {
            canTakeAction: true
        };
    }
    canPlayerBuyDevelopmentCard(player) {
        const isSomePlayerDiscarding = this.currentStateValidator.isSomePlayerInActionState(ActionState.SelectCardsToDiscard);
        if (isSomePlayerDiscarding) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.playersDiscarding'
            }
        };
        const isDevelopmentCardAvailableInBank = this.bankDevelopmentCards.cards.length > 0;
        if (!isDevelopmentCardAvailableInBank) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.noDevelopmentCardsInBank'
            }
        };
        const hasResourcesForDevelopmentCard = this.hasResourcesForDevelopmentCard(player).canTakeAction;
        if (!hasResourcesForDevelopmentCard) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.notEnoughResources'
            }
        };
        return {
            canTakeAction: true
        };
    }
    canPlayerUseAtLeastOneDevelopmentCard(player) {
        for (const card of this.getDevelopmentCardsPlayerState(player).developmentCards.cards){
            if (card == CardEnum.VictoryPoint) continue;
            if (!this.canPlayerUseDevelopmentCard(player, card).canTakeAction) continue;
            return true;
        }
        return false;
    }
    getPlayersTotalDevelopmentCards(player) {
        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player);
        return devCardsPlayerState.developmentCards.cards.length;
    }
    getPlayersTotalDevelopmentCardsOfType(player, cardEnum) {
        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player);
        const developmentCards = new CardValidatorOrdered(devCardsPlayerState.developmentCards);
        return ArrayUtils.countOccurenceOfValueInArray(developmentCards.cards, cardEnum);
    }
    getPlayersTotalUsedDevelopmentCardsOfType(player, cardEnum) {
        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player);
        return ArrayUtils.countOccurenceOfValueInArray(devCardsPlayerState.developmentCardsUsed, cardEnum);
    }
    getTotalDevelopmentCardsInBank() {
        return this.bankDevelopmentCards.getNumberOfCardsInInventory();
    }
    static getErrorMessageIfBlockedByOpponentActionState(card) {
        switch(card){
            case CardEnum.Knight:
                return {
                    canTakeAction: false,
                    errorMessageIfCantTakeAction: {
                        key: 'strings:game.errors.knightUsageBlockedByOpponentAction'
                    }
                };
            default:
                return {
                    canTakeAction: false,
                    errorMessageIfCantTakeAction: {
                        key: 'strings:game.errors.developmentCardBlockedByOpponentAction'
                    }
                };
        }
    }
    constructor(playerValidators, mapValidator, currentStateValidator, developmentCardsState){
        super(playerValidators, mapValidator);
        this.currentStateValidator = currentStateValidator;
        this.bankDevelopmentCards = new CardValidatorOrdered(developmentCardsState.bankDevelopmentCards);
        this.playerDevelopmentCardStates = new Map();
        for(const player in developmentCardsState.players){
            const playerColor = Number(player);
            const playerDevelopmentCardState = developmentCardsState.players[playerColor];
            if (playerDevelopmentCardState == undefined) continue;
            this.playerDevelopmentCardStates.set(playerColor, new MechanicValidatorDevelopmentCardPlayer(playerDevelopmentCardState));
        }
        this.playerDevelopmentCardStates.set(PlayerColor.None, new MechanicValidatorDevelopmentCardPlayer({
            developmentCards: {
                cards: []
            },
            developmentCardsUsed: []
        }));
    }
}
